import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl, headersDefault } from "../actions";
import {
  axiosGetRead,
  axiosRead,
  friendlyReadError,
} from "../../services/readRequest";

const LIMIT_DEFAULT = 20;

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatGramsIT = (v) => {
  const n = toNum(v);
  if (n === null) return "--";
  return `${n.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}g`;
};

const pickNutrient = (nutrients, includesName) => {
  if (!Array.isArray(nutrients)) return null;
  const found = nutrients.find((n) =>
    String(n?.nutrient?.name || "")
      .toLowerCase()
      .includes(String(includesName).toLowerCase()),
  );
  return toNum(found?.quantity);
};

const mapRecipe = (item) => {
  const a = item?.attributes || {};
  const nutrients = a?.nutrients || [];
  const userRating =
    toNum(
      a?.my_rating ??
        a?.myRating ??
        a?.user_rating ??
        a?.userRating ??
        a?.rating?.my ??
        a?.rating?.value,
    ) ?? 0;

  return {
    id: item?.id,
    name: a?.name || "",
    description: a?.description ?? null,

    imageUrl: a?.images?.[0]?.url || "",
    hasVideo: Boolean(a?.vimeo_id),

    categoryName: a?.category?.attributes?.name || "",

    // For the list's "10 min | Facile" row.
    preparation_time: a?.preparation_time ?? null,
    cooking_time: a?.cooking_time ?? null,
    difficulty: a?.difficulty ?? null,

    ratingAverage: toNum(a?.rating?.average) ?? 0,
    ratingCount: toNum(a?.rating?.count) ?? 0,
    userRating,

    reaction: a?.reaction ?? null,

    nutrients: {
      carb: pickNutrient(nutrients, "Carboidrati"),
      prot: pickNutrient(nutrients, "Proteine"),
      lip: pickNutrient(nutrients, "Lipidi"),
      fib: pickNutrient(nutrients, "Fibra"),
    },

    _fmt: {
      carb: formatGramsIT(pickNutrient(nutrients, "Carboidrati")),
      prot: formatGramsIT(pickNutrient(nutrients, "Proteine")),
      lip: formatGramsIT(pickNutrient(nutrients, "Lipidi")),
      fib: formatGramsIT(pickNutrient(nutrients, "Fibra")),
    },
  };
};

const applyRecipeRating = (recipe, nextRating) => {
  if (!recipe) return;

  const next = Math.max(0, Math.min(5, Number(nextRating) || 0));
  const prev = Math.max(0, Math.min(5, Number(recipe.userRating) || 0));
  const prevCount = Math.max(0, Number(recipe.ratingCount) || 0);

  recipe.userRating = next;
  if (prev === 0 && next > 0) recipe.ratingCount = prevCount + 1;
  else if (prev > 0 && next === 0) recipe.ratingCount = Math.max(0, prevCount - 1);

  if (next > 0) recipe.ratingAverage = next;
  else if (prev > 0 && prevCount <= 1) recipe.ratingAverage = 0;
};

const normalizeMeta = (
  meta,
  fallbackPage = 1,
  fallbackLimit = LIMIT_DEFAULT,
) => {
  const current_page = Number(meta?.current_page ?? fallbackPage);
  const last_page = Number(meta?.last_page ?? current_page);
  const per_page = Number(meta?.per_page ?? fallbackLimit);
  const total = Number(meta?.total ?? 0);

  return {
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
    hasMore: current_page < last_page,
  };
};

const buildNutrientsPayload = (rangesMap) => {
  const entries = Object.entries(rangesMap || {});
  return entries
    .map(([id, v]) => ({
      id: Number(id),
      min: Number(v?.lower ?? 0),
      max: Number(v?.upper ?? 200),
    }))
    .filter((x) => Number.isFinite(x.id))
    .filter((x) => !(x.min === 0 && x.max === 200));
};

const getSeasonCategory = (filterCategories) =>
  (filterCategories || []).find(
    (c) =>
      String(c?.name || "")
        .trim()
        .toLowerCase() === "stagione",
  );

const buildCurrentSeasonFilterIds = (filterCategories, seasonsData) => {
  const seasonCat = getSeasonCategory(filterCategories);
  if (!seasonCat) return [];

  const labelToId = new Map(
    (seasonCat.filters || []).map((f) => [
      String(f?.label || "")
        .trim()
        .toLowerCase(),
      Number(f?.id),
    ]),
  );

  const current = seasonsData?.current_seasons || [];
  const ids = current
    .map((s) =>
      labelToId.get(
        String(s || "")
          .trim()
          .toLowerCase(),
      ),
    )
    .filter((x) => Number.isFinite(x));

  return Array.from(new Set(ids));
};

const hasManualSeasonSelection = (advancedFilterIds, filterCategories) => {
  const seasonCat = getSeasonCategory(filterCategories);
  if (!seasonCat) return false;

  const seasonIds = new Set((seasonCat.filters || []).map((f) => Number(f.id)));
  return (advancedFilterIds || []).some((id) => seasonIds.has(Number(id)));
};

export const findFilterIdByCategoryAndLabel = (
  filterCategories,
  categoryName,
  labelMatcher,
) => {
  const target = String(categoryName || "")
    .trim()
    .toLowerCase();
  const cat = (filterCategories || []).find(
    (c) => String(c?.name || "").trim().toLowerCase() === target,
  );
  if (!cat) return null;
  const f = (cat.filters || []).find((x) =>
    labelMatcher(String(x?.label || "").trim().toLowerCase()),
  );
  return f ? Number(f.id) : null;
};

/* ----------------------- THUNKS ----------------------- */

export const fetchRecipeFilters = createAsyncThunk(
  "recipe/fetchRecipeFilters",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosGetRead(
        `${baseUrl}/api/v1/recipes/filters/categories/list`,
        { headers: headersDefault() },
      );
      return res.data;
    } catch (e) {
      return rejectWithValue(friendlyReadError(e));
    }
  },
);

export const fetchRecipeNutrients = createAsyncThunk(
  "recipe/fetchRecipeNutrients",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosGetRead(`${baseUrl}/api/v1/recipes/nutrients/list`, {
        headers: headersDefault(),
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(friendlyReadError(e));
    }
  },
);

export const fetchRecipeSeasons = createAsyncThunk(
  "recipe/fetchRecipeSeasons",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosGetRead(`${baseUrl}/api/v1/recipes/seasons/list`, {
        headers: headersDefault(),
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(friendlyReadError(e));
    }
  },
);

export const fetchRecipes = createAsyncThunk(
  "recipe/fetchRecipes",
  async ({ page = 1, append = false } = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState().recipes;
      const limit = state.limit || LIMIT_DEFAULT;

      if (state.mode === "favorites") {
        // The same search and category chips as the Ricettario. The chip id is
        // a filter id, sent as filters[] exactly as /recipes/search takes it.
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        const favTerm = String(state.favTerm || "").trim();
        if (favTerm) params.set("term", favTerm);
        if (state.favCategoryId && String(state.favCategoryId) !== "all") {
          params.append("filters[]", String(state.favCategoryId));
        }

        const res = await axiosGetRead(
          `${baseUrl}/api/v1/recipes/favorites/list?${params.toString()}`,
          { headers: headersDefault() },
        );

        const items = (res.data?.data || []).map(mapRecipe);
        return {
          items,
          meta: normalizeMeta(res.data?.meta, page, limit),
          append,
        };
      }

      // The search term is sent unconditionally — including when a quick
      // category is active. Previously the term was forced to "" whenever a
      // category was selected, which made the search bar inert after picking
      // a category. The backend `/recipes/search` endpoint accepts term +
      // filters together (verified against the local API), so there's no
      // reason to suppress one when the other is set.
      const term = String(state.term || "").trim();
      const hasQuickCategory =
        state.quickCategoryId && String(state.quickCategoryId) !== "all";

      const nutrientsPayload = buildNutrientsPayload(state.nutrientRanges);

      const ids = [
        ...(Array.isArray(state.advancedFilterIds)
          ? state.advancedFilterIds
          : []),
        hasQuickCategory ? state.quickCategoryId : null,
        Number.isFinite(Number(state.portions)) ? Number(state.portions) : null, // ✅ 43/44
      ]
        .filter(Boolean)
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x));

      let uniqueFilters = Array.from(new Set(ids));

      // Add explicitly selected season filter IDs
      if (Array.isArray(state.selectedSeasons) && state.selectedSeasons.length > 0) {
        uniqueFilters = Array.from(
          new Set([...uniqueFilters, ...state.selectedSeasons.map(Number).filter(Number.isFinite)]),
        );
      }

      // Esclusioni: read current toggle state. Both default to OFF; the
      // user opts in via the FilterModal checkboxes. We also need to
      // know whether the user has a current path — if not, the
      // path-foods filter has nothing to do on the backend, so we omit
      // it from the wire payload (backend will default to true and
      // silently no-op since the user has no current protocol, but
      // explicit `false` is cleaner and keeps cache keys deterministic).
      const excludeUndesirables = state.excludeUndesirables === true;
      const rootState = getState();
      const hasCurrentPath = Boolean(
        rootState?.user?.master_club_subscriber?.protocols?.current?.protocol_id,
      );
      const excludePathFoods =
        hasCurrentPath && state.excludePathFoods === true;

      // Default behaviour (both toggles ON) keeps the old fallthrough
      // semantics — empty filters/term route to /randomly/list. But when
      // EITHER toggle is OFF the user has made an explicit "show me more"
      // choice that the random endpoint can't honour (it doesn't accept
      // the flags), so we force the search endpoint even with no other
      // filters. Empty term + empty filters on /recipes/search is valid.
      const exclusionsDiverge = !excludeUndesirables || (hasCurrentPath && !excludePathFoods);

      const shouldSearch =
        term.length > 0 ||
        uniqueFilters.length > 0 ||
        nutrientsPayload.length > 0 ||
        exclusionsDiverge || Boolean(state.ratingBand);

      if (shouldSearch) {
        const body = {
          ...(term.length > 0 ? { term } : {}),
          filters: uniqueFilters,
          ...(state.ratingBand ? { rating_band: state.ratingBand } : {}),
          nutrients: nutrientsPayload,
          page,
          limit,
          exclude_undesirables: excludeUndesirables,
          exclude_path_foods: excludePathFoods,
        };

        const res = await axiosRead(
          {
            method: "POST",
            url: `${baseUrl}/api/v1/recipes/search`,
            data: body,
            headers: headersDefault(),
          },
          // Bypass the in-flight de-dupe: a browse list is randomized server-side,
          // so re-selecting the same category (identical request body) must issue
          // a genuinely fresh request and get a new random order — not be served
          // the previous in-flight/settled promise for the same key.
          { dedupe: false },
        );

        const items = (res.data?.data || []).map(mapRecipe);
        return {
          items,
          meta: normalizeMeta(res.data?.meta, page, limit),
          append,
        };
      }

      const res = await axiosGetRead(
        `${baseUrl}/api/v1/recipes/randomly/list?page=${page}&limit=${limit}`,
        { headers: headersDefault() },
        // Same reason as the search path: keep every browse fetch fresh so the
        // randomized order actually changes on each entry / re-selection.
        { dedupe: false },
      );

      const items = (res.data?.data || []).map(mapRecipe);
      return {
        items,
        meta: normalizeMeta(res.data?.meta, page, limit),
        append,
      };
    } catch (e) {
      return rejectWithValue(friendlyReadError(e));
    }
  },
);

export const loadMoreRecipes = createAsyncThunk(
  "recipe/loadMoreRecipes",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const s = getState().recipes;
      if (!s.pagination?.hasMore) return { skipped: true };
      const nextPage = Number(s.pagination.currentPage || 1) + 1;
      return await dispatch(
        fetchRecipes({ page: nextPage, append: true }),
      ).unwrap();
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

export const toggleRecipeLike = createAsyncThunk(
  "recipe/toggleRecipeLike",
  async ({ recipeId, currentlyLiked }, { rejectWithValue }) => {
    try {
      if (currentlyLiked) {
        await axios.post(
          `${baseUrl}/api/v1/reactions/unreact`,
          { type: "recipe", id: recipeId },
          { headers: headersDefault() },
        );
        return { recipeId, liked: false };
      }

      await axios.post(
        `${baseUrl}/api/v1/reactions/react`,
        { type: "recipe", id: recipeId, reaction: "like" },
        { headers: headersDefault() },
      );
      return { recipeId, liked: true };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/**
 * The per-user "Provata" marker — the same `completed` reaction videos use for
 * Fatto, so each row records who tried it and when.
 */
export const toggleRecipeCompleted = createAsyncThunk(
  "recipe/toggleCompleted",
  async ({ recipeId, completed }, { rejectWithValue }) => {
    try {
      const body = { id: recipeId, type: "recipe", reaction: "completed" };
      await axios({
        url: `${baseUrl}/api/v1/reactions/react`,
        method: completed ? "DELETE" : "POST",
        data: body,
        headers: headersDefault(),
      });
      return { recipeId, completed: !completed };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

export const rateRecipe = createAsyncThunk(
  "recipe/rateRecipe",
  async ({ recipeId, rating }, { rejectWithValue }) => {
    try {
      const nextRating = Math.max(0, Math.min(5, Number(rating) || 0));
      if (!nextRating) {
        await axios.delete(`${baseUrl}/api/v1/recipes/${recipeId}/unrate`, {
          headers: headersDefault(),
        });
      } else {
        await axios.put(
          `${baseUrl}/api/v1/recipes/${recipeId}/rate/${nextRating}`,
          null,
          { headers: headersDefault() },
        );
      }
      return { recipeId, rating: nextRating };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/* ----------------------- SLICE ----------------------- */

const recipeSlice = createSlice({
  name: "recipe",
  initialState: {
    mode: "all",

    term: "",
    quickCategoryId: "all",
    advancedFilterIds: [],
    ratingBand: null,

    // Search and category chip of the favourites view. Kept apart from the
    // Ricettario's own term/quickCategoryId, which switching view preserves.
    favTerm: "",
    favCategoryId: "all",

    // Only the newest recipe list request may write the list: a slow
    // response to an older search must not replace a newer one.
    latestListRequestId: null,

    // ✅ Porzioni is now selected FILTER ID (43/44) or null (no default)
    portions: null,

    filterCategories: [],
    quickCategories: [{ id: "all", label: "Tutte" }],

    nutrientsCatalog: [],
    seasonsData: null,
    nutrientRanges: {},
    selectedSeasons: [], // array of season filter IDs (replaces boolean seasonal)
    filtersTouched: false,

    // ── Esclusioni toggles (Recipe Finder filter modal) ────────────────
    // Both default to true (protective baseline) and reset to true on
    // every reset path. When either is false, fetchRecipes forces the
    // POST /recipes/search endpoint instead of falling through to
    // /randomly/list, so the user's "show me everything" choice is
    // actually honoured even with no other filters set.
    excludeUndesirables: false,
    excludePathFoods: false,

    listScroll: { key: "", top: 0 },

    items: [],
    // Standard meaning: `true` only when a fetch is in flight.
    // (A previous attempt to default this to `true` to prevent the
    // initial-empty-state flash collided with the page's existing
    // `if (loading) return` guard inside the mount useEffect — which
    // then never dispatched fetchRecipes, leaving the spinner up
    // forever. The empty-state flash is now handled via the
    // `hasFetched` flag below instead.)
    loading: false,
    // True once the first fetchRecipes has settled (either fulfilled
    // or rejected). Used by the Recipes page to show the spinner from
    // the very first render until a fetch resolves, so the empty
    // state "Nessuna ricetta trovata" doesn't briefly flash before
    // the request even starts.
    hasFetched: false,
    error: null,
    ratingRollback: {},

    limit: LIMIT_DEFAULT,
    pagination: {
      currentPage: 1,
      lastPage: 1,
      perPage: LIMIT_DEFAULT,
      total: 0,
      hasMore: false,
    },
  },
  reducers: {
    setMode(state, action) {
      state.mode = action.payload === "favorites" ? "favorites" : "all";
      if (state.mode === "favorites") {
        // Opening the favourites always starts from the whole list.
        state.favTerm = "";
        state.favCategoryId = "all";
      }
      state.items = [];
      state.error = null;
      // Reset hasFetched so the next render of the page shows the
      // spinner during the favorites/all refetch instead of briefly
      // flashing the empty state of the new mode. fetchRecipes.fulfilled
      // sets hasFetched back to true once results land.
      state.hasFetched = false;
      state.pagination = {
        currentPage: 1,
        lastPage: 1,
        perPage: state.limit,
        total: 0,
        hasMore: false,
      };
    },
    setTerm(state, action) {
      state.term = action.payload ?? "";
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },
    setQuickCategoryId(state, action) {
      state.quickCategoryId = action.payload ?? "all";
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },
    setFavTerm(state, action) {
      state.favTerm = action.payload ?? "";
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },
    setFavCategoryId(state, action) {
      state.favCategoryId = action.payload ?? "all";
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },
    setAdvancedFilterIds(state, action) {
      state.advancedFilterIds = Array.isArray(action.payload)
        ? action.payload
        : [];
      state.filtersTouched = true;
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },

    // ✅ Porzioni: store filter id 43/44 or null
    setPortions(state, action) {
      const v = Number(action.payload);
      state.portions = Number.isFinite(v) ? v : null;
      state.filtersTouched = true;
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },

    setRatingBand(state, action) {
      state.ratingBand = [3, 4, 5].includes(Number(action.payload)) ? Number(action.payload) : null;
      state.filtersTouched = true;
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },

    setNutrientRanges(state, action) {
      state.nutrientRanges =
        action.payload && typeof action.payload === "object"
          ? action.payload
          : {};
      state.filtersTouched = true;
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },

    setSelectedSeasons(state, action) {
      state.selectedSeasons = Array.isArray(action.payload) ? action.payload : [];
      state.filtersTouched = true;
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },

    setExcludeUndesirables(state, action) {
      state.excludeUndesirables = action.payload !== false;
      state.filtersTouched = true;
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },

    setExcludePathFoods(state, action) {
      state.excludePathFoods = action.payload !== false;
      state.filtersTouched = true;
      state.items = [];
      state.error = null;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },

    setListScroll(state, action) {
      const key = String(action.payload?.key || "");
      const top = Number(action.payload?.top || 0);
      state.listScroll = {
        key,
        top: Number.isFinite(top) ? top : 0,
      };
    },

    resetAllFilters(state) {
      state.term = "";
      state.quickCategoryId = "all";
      state.advancedFilterIds = [];
      state.ratingBand = null;
      state.nutrientRanges = {};
      state.selectedSeasons = [];
      state.filtersTouched = false;
      state.portions = null;
      state.excludeUndesirables = false;
      state.excludePathFoods = false;

      state.items = [];
      state.error = null;
      state.pagination = {
        currentPage: 1,
        lastPage: 1,
        perPage: state.limit,
        total: 0,
        hasMore: false,
      };
    },

    // Narrow reset for the FilterModal's "Reset" button. Clears every modal-
    // owned filter (advanced chips, season chips, portion chip, macro ranges)
    // and resets `filtersTouched` to false — but deliberately KEEPS `term`
    // and `quickCategoryId`, which live outside the modal. After this fires:
    //   - The recipe list refetches with whatever the user had searched or
    //     categorised (random list if neither, category-only if a category
    //     is active, search results if a term is active).
    //   - The next time the modal opens, the init effect takes the
    //     "untouched" branch and re-suggests the user's preference defaults
    //     (Onnivoro/Vegetariano + intolerances + 1 porzione + current
    //     seasons) as pre-selected chips.
    resetModalFilters(state) {
      state.advancedFilterIds = [];
      state.ratingBand = null;
      state.nutrientRanges = {};
      state.selectedSeasons = [];
      state.portions = null;
      state.excludeUndesirables = false;
      state.excludePathFoods = false;
      state.filtersTouched = false;

      state.items = [];
      state.error = null;
      state.pagination = {
        currentPage: 1,
        lastPage: 1,
        perPage: state.limit,
        total: 0,
        hasMore: false,
      };
    },

    resetFinder(state) {
      state.mode = "all";
      state.term = "";
      state.quickCategoryId = "all";
      state.advancedFilterIds = [];
      state.ratingBand = null;
      state.nutrientRanges = {};
      state.selectedSeasons = [];
      state.filtersTouched = false;
      state.portions = null;
      state.excludeUndesirables = false;
      state.excludePathFoods = false;

      state.items = [];
      state.error = null;
      state.pagination = {
        currentPage: 1,
        lastPage: 1,
        perPage: state.limit,
        total: 0,
        hasMore: false,
      };
    },

    // Apply defaults derived from user profile without marking filtersTouched.
    // Lets the modal still treat state as "untouched" so Reset re-applies defaults.
    setDefaultsFromProfile(state, action) {
      const { portions, advancedFilterIds } = action.payload || {};
      // null means "no servings filter", which has to clear the value rather
      // than be coerced: Number(null) is 0, and 0 passes isFinite, so the old
      // check quietly stored a filter id of 0.
      if (portions === null || portions === undefined) {
        state.portions = null;
      } else if (Number.isFinite(Number(portions))) {
        state.portions = Number(portions);
      }
      if (Array.isArray(advancedFilterIds)) {
        state.advancedFilterIds = advancedFilterIds
          .map(Number)
          .filter(Number.isFinite);
      }
      state.items = [];
      state.error = null;
      // Reset hasFetched so the Recipes page useEffect re-fires its
      // initial fetch with the freshly-seeded filter defaults. The
      // page's effect uses `!hasFetched` as the gate (NOT items.length)
      // because an empty result is a legitimate state — keying off
      // items.length === 0 caused an infinite refetch loop when the
      // API legitimately returned 0 recipes.
      state.hasFetched = false;
      state.pagination.currentPage = 1;
      state.pagination.hasMore = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecipeFilters.fulfilled, (state, action) => {
        const cats = Array.isArray(action.payload)
          ? action.payload
          : Array.isArray(action.payload?.data)
          ? action.payload.data
          : [];

        const removedIds = [];

        const mapped = cats.map((c) => {
          const name = c?.attributes?.name || "";
          const isDietType =
            String(name).trim().toLowerCase() === "regime alimentare";

          const filters = (c?.attributes?.filters || [])
            .map((f) => ({
              id: f?.id,
              label: f?.attributes?.label || "",
            }))
            .filter((f) => {
              if (!isDietType) return true;
              const lbl = String(f.label || "")
                .trim()
                .toLowerCase();
              const isRemoved = lbl === "ovo-pesco vegetale";
              if (isRemoved) removedIds.push(Number(f.id));
              return !isRemoved;
            });

          return {
            id: c?.id,
            name,
            multiple: Boolean(c?.attributes?.multiple),
            filters,
          };
        });

        state.filterCategories = mapped;

        if (removedIds.length > 0 && Array.isArray(state.advancedFilterIds)) {
          const removedSet = new Set(
            removedIds.filter((x) => Number.isFinite(x)),
          );
          state.advancedFilterIds = state.advancedFilterIds.filter(
            (id) => !removedSet.has(Number(id)),
          );
        }

        const categoria = mapped.find(
          (c) => String(c.name || "").toLowerCase() === "categoria",
        );

        state.quickCategories = [
          { id: "all", label: "Tutte" },
          // Sort categories alphabetically (Italian collation), keeping "Tutte"
          // pinned first.
          ...(categoria?.filters || [])
            .slice()
            .sort((a, b) =>
              String(a.label || "").localeCompare(String(b.label || ""), "it", {
                sensitivity: "base",
              }),
            )
            .map((f) => ({
              id: f.id,
              label: f.label,
            })),
        ];
      })
      .addCase(fetchRecipeNutrients.fulfilled, (state, action) => {
        state.nutrientsCatalog = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchRecipeSeasons.fulfilled, (state, action) => {
        state.seasonsData = action.payload || null;
      })
      .addCase(fetchRecipes.pending, (state, action) => {
        state.latestListRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecipes.fulfilled, (state, action) => {
        // A newer search or chip was picked while this one was on its way.
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.loading = false;
        // First fetch has now settled — empty state may safely show
        // if `items` is also empty after this assignment.
        state.hasFetched = true;
        const { items, meta, append } = action.payload || {};
        state.items = append ? [...state.items, ...(items || [])] : items || [];
        if (meta) state.pagination = meta;
      })
      .addCase(fetchRecipes.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.loading = false;
        // Mark as fetched on rejection too, otherwise a failing fetch
        // would keep the page in a permanent spinner state.
        state.hasFetched = true;
        state.error = action.payload || action.error?.message || "Errore";
      })
      .addCase(loadMoreRecipes.pending, (state) => {
        state.error = null;
      })
      .addCase(loadMoreRecipes.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Errore";
      })
      .addCase(rateRecipe.pending, (state, action) => {
        const { recipeId, rating } = action.meta.arg || {};
        if (!recipeId) return;
        const idx = state.items.findIndex(
          (r) => Number(r.id) === Number(recipeId),
        );
        if (idx === -1) return;
        state.ratingRollback[recipeId] = {
          userRating: state.items[idx].userRating,
          ratingAverage: state.items[idx].ratingAverage,
          ratingCount: state.items[idx].ratingCount,
        };
        applyRecipeRating(state.items[idx], rating);
      })
      .addCase(rateRecipe.fulfilled, (state, action) => {
        const { recipeId } = action.payload || {};
        if (recipeId) delete state.ratingRollback[recipeId];
      })
      .addCase(rateRecipe.rejected, (state, action) => {
        const { recipeId } = action.meta.arg || {};
        const rollback = state.ratingRollback[recipeId];
        if (rollback) {
          const idx = state.items.findIndex(
            (r) => Number(r.id) === Number(recipeId),
          );
          if (idx !== -1) {
            state.items[idx].userRating = rollback.userRating;
            state.items[idx].ratingAverage = rollback.ratingAverage;
            state.items[idx].ratingCount = rollback.ratingCount;
          }
          delete state.ratingRollback[recipeId];
        }
        state.error = action.payload || action.error?.message || "Errore";
      })
      .addCase(toggleRecipeLike.fulfilled, (state, action) => {
        const { recipeId, liked } = action.payload || {};
        if (!recipeId) return;

        const idx = state.items.findIndex(
          (r) => Number(r.id) === Number(recipeId),
        );
        if (idx === -1) return;

        if (liked) {
          state.items[idx].reaction = { attributes: { reaction: "like" } };
        } else {
          state.items[idx].reaction = null;
          if (state.mode === "favorites") {
            state.items = state.items.filter(
              (r) => Number(r.id) !== Number(recipeId),
            );
            state.pagination.total = Math.max(
              0,
              Number(state.pagination.total || 0) - 1,
            );
          }
        }
      })
      .addCase(toggleRecipeLike.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Errore";
      });
  },
});

export const {
  setMode,
  setTerm,
  setQuickCategoryId,
  setFavTerm,
  setFavCategoryId,
  setAdvancedFilterIds,
  setNutrientRanges,
  setRatingBand,
  setSelectedSeasons,
  setPortions,
  setExcludeUndesirables,
  setExcludePathFoods,
  setListScroll,
  resetAllFilters,
  resetModalFilters,
  resetFinder,
  setDefaultsFromProfile,
} = recipeSlice.actions;

export default recipeSlice.reducer;
